import { useEffect, useMemo, useState } from 'react'
import type { FamilyDto } from '../add-item/types'
import type { FamilyEditorSchemaDto } from './types'
import { getCatalogFamilyEditorSchema } from './catalog-editor-schema.api'
import type { LineRequestDto, LineResponseDto } from '@/components/features/project-detail/quotes/types'
import { addQuoteVersionLineV2, updateQuoteVersionLineV2 } from '@/components/features/project-detail/quotes/quotes-v2.api'
import { FamilyEditorSidebarHeader } from './FamilyEditorSidebarHeader'
import { FamilyEditorSidebarFooter } from './FamilyEditorSidebarFooter'
import {
  FamilyEditorSidebarContentFallback,
  type FamilyEditorSidebarContentProps,
} from './FamilyEditorSidebarContentFallback'
import { getFamilyEditorSidebarContentByFamilyKey } from './family-editor-sidebar-content-registry'
import { ApiError } from '@/lib/api/client'
import { parseDecimalInput } from '@/lib/decimal'

interface FamilyEditorSidebarProps {
  open: boolean
  onClose: () => void
  family: FamilyDto | null
  projectId: string
  quoteId: string | null
  versionId: string | null
  mode: 'create' | 'update'
  lineId?: string | null
  initialLine?: LineResponseDto | null
  onSubmitted: () => void | Promise<void>
}

export function FamilyEditorSidebar({
  open,
  onClose,
  family,
  projectId,
  quoteId,
  versionId,
  mode,
  lineId,
  initialLine,
  onSubmitted,
}: FamilyEditorSidebarProps) {
  const [schema, setSchema] = useState<FamilyEditorSchemaDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [quantity, setQuantity] = useState<number | null>(null)
  const [unitPrice, setUnitPrice] = useState<number | null>(null)
  const [vatPercent, setVatPercent] = useState<number | null>(null)
  const [priceIncluded, setPriceIncluded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  function readCondition(condition: unknown): { attributeId: string; equalsAny: string[] } | null {
    if (!condition || typeof condition !== 'object') return null
    const c = condition as Record<string, unknown>
    const attributeId = (c.attributeId ?? c.AttributeId) as unknown
    const equalsAny = (c.equalsAny ?? c.EqualsAny) as unknown
    if (typeof attributeId !== 'string') return null
    if (!Array.isArray(equalsAny)) return null
    const normalized = equalsAny.filter((v) => typeof v === 'string') as string[]
    return { attributeId, equalsAny: normalized }
  }

  const coerceAttributesForSubmit = (schema: FamilyEditorSchemaDto, attrs: Record<string, unknown>) => {
    const next: Record<string, unknown> = { ...attrs }

    ;(schema.fields || []).forEach((f) => {
      const rawType = (f.type || '').toLowerCase()
      if (rawType !== 'number') return

      const v = next[f.id]
      if (typeof v === 'number') return
      if (v == null) return

      if (typeof v === 'string') {
        const trimmed = v.trim()
        if (trimmed.length === 0) {
          next[f.id] = undefined
          return
        }

        const parsed = parseDecimalInput(trimmed)
        next[f.id] = parsed == null ? null : parsed
        return
      }

      const parsed = parseDecimalInput(String(v))
      next[f.id] = parsed == null ? null : parsed
    })

    return next
  }

  function isConditionMet(condition: unknown): boolean {
    const parsed = readCondition(condition)
    if (!parsed) return true
    const v = values[parsed.attributeId]
    if (v == null) return false
    return parsed.equalsAny.includes(String(v))
  }

  function getFieldVisibility(field: FamilyEditorSchemaDto['fields'][number]) {
    if (!field.visibleIf) return true
    return isConditionMet(field.visibleIf)
  }

  function getFieldIsRequired(field: FamilyEditorSchemaDto['fields'][number]) {
    const requiredBase = field.required === true
    if (!requiredBase) return false
    if (!field.requiredIf) return true
    return isConditionMet(field.requiredIf)
  }

  const visibleFields = useMemo(() => {
    if (!schema) return [] as FamilyEditorSchemaDto['fields']
    return (schema.fields || []).filter((f) => getFieldVisibility(f))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values])

  const allVisibleFields = useMemo(() => {
    return visibleFields
  }, [visibleFields])

  useEffect(() => {
    if (!open || !family) return

    let isMounted = true

    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)
        setSchema(null)

        const response = await getCatalogFamilyEditorSchema(family.familyId, projectId, versionId)
        if (isMounted) {
          setSchema(response)
        }
      } catch {
        if (isMounted) {
          setError('Erreur lors du chargement du schéma')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    load()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, family?.familyId, projectId])

  useEffect(() => {
    if (!schema) return

    const nextValues: Record<string, unknown> = {}
    ;(schema.fields || []).forEach((f) => {
      if (f.defaultValue !== undefined) {
        nextValues[f.id] = f.defaultValue
      }
    })

    if (mode === 'update' && initialLine?.attributes && typeof initialLine.attributes === 'object') {
      Object.assign(nextValues, initialLine.attributes)
    }

    setValues(nextValues)

    if (mode === 'update' && typeof initialLine?.quantity === 'number') {
      setQuantity(initialLine.quantity)
    } else {
      setQuantity(1)
    }

    if (mode === 'update' && typeof initialLine?.unitPrice === 'number') {
      setUnitPrice(initialLine.unitPrice)
    } else {
      setUnitPrice(null)
    }

    // Apply defaultVatPercent from the project construction type regardless of vatRateEditable.
    // vatRateEditable only controls whether the SELECT is shown in the UI, not whether
    // the project default should be sent to the backend (it always should be).
    const defaultVat =
      typeof schema.pricing?.defaultVatPercent === 'number'
        ? schema.pricing.defaultVatPercent
        : null
    const nextVat = mode === 'update' && typeof initialLine?.vatPercent === 'number' ? initialLine.vatPercent : defaultVat
    setVatPercent(nextVat)
    setPriceIncluded(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema])

  const missingRequiredFields = useMemo(() => {
    if (!schema) return [] as Array<{ id: string; label: string }>

    return (schema.fields || [])
      .filter((f) => getFieldVisibility(f) && getFieldIsRequired(f))
      .filter((f) => {
        const v = values[f.id]
        if (v == null) return true
        if (typeof v === 'string') return v.trim().length === 0
        return false
      })
      .map((f) => ({ id: f.id, label: f.label }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values])

  const missingRequiredFieldIds = useMemo(() => missingRequiredFields.map((f) => f.id), [missingRequiredFields])

  const ContentComponent = useMemo(() => {
    const familyKey = family?.familyKey
    return getFamilyEditorSidebarContentByFamilyKey(familyKey) ?? FamilyEditorSidebarContentFallback
  }, [family?.familyKey])

  const contentProps: FamilyEditorSidebarContentProps = {
    projectId,
    quoteId,
    versionId,
    familyKey: family?.familyKey ?? null,
    schema,
    isLoading,
    error,
    missingRequiredFields,
    missingRequiredFieldIds,
    allVisibleFields,
    values,
    onChangeValues: setValues,
    isFieldRequired: getFieldIsRequired,
    quantity,
    onQuantityChange: setQuantity,
    unitPrice,
    onUnitPriceChange: setUnitPrice,
    vatPercent,
    onVatPercentChange: setVatPercent,
    priceIncluded,
    onPriceIncludedChange: setPriceIncluded,
  }

  const handleSubmit = async () => {
    if (!family) return
    if (!quoteId || !versionId) {
      setError("Erreur: contexte du devis manquant")
      return
    }
    if (!schema) return
    if (mode === 'update' && !lineId) {
      setError('Erreur: ligne introuvable')
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)

      const coercedAttributes = coerceAttributesForSubmit(schema, values)

      const request: LineRequestDto = {
        kind: '',
        title: family.familyLabel,
        description: null,
        categoryId: null,
        categoryLabel: null,
        familyId: family.familyId,
        familyKey: family.familyKey,
        attributes: coercedAttributes,
        quantity: typeof quantity === 'number' && Number.isFinite(quantity) ? quantity : null,
        unitPrice: typeof unitPrice === 'number' && Number.isFinite(unitPrice) ? unitPrice : null,
        vatPercent: typeof vatPercent === 'number' && Number.isFinite(vatPercent) ? vatPercent : null,
        amount: null,
      }

      if (mode === 'update' && lineId) {
        await updateQuoteVersionLineV2(projectId, quoteId, versionId, lineId, request)
      } else {
        await addQuoteVersionLineV2(projectId, quoteId, versionId, request)
      }
      await onSubmitted()
    } catch (e) {
      const base = mode === 'update' ? 'Erreur lors de la mise à jour de la ligne' : "Erreur lors de l'ajout de la ligne"
      if (e instanceof ApiError && e.message) {
        setError(`${base}: ${e.message}`)
      } else {
        setError(base)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!open || !family) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 animate-in fade-in duration-200" onClick={onClose} />

      <div className="fixed right-0 top-0 bottom-0 w-full md:w-[720px] bg-background z-50 shadow-2xl animate-in slide-in-from-right duration-300 overflow-hidden flex flex-col">
        <FamilyEditorSidebarHeader family={family} mode={mode} onClose={onClose} />

        <ContentComponent {...contentProps} />

        <FamilyEditorSidebarFooter
          mode={mode}
          isSubmitting={isSubmitting}
          submitDisabled={isSubmitting || !schema || !quoteId || !versionId}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  )
}
